import React, { useEffect, useState, useCallback } from "react";
import { getFromHub, postToHub } from "../../network/actions";
import { formatDate } from "../../utils/dateUtils";
import { useNotificationContext } from "../../context/NotificationContext";
import { parseUserAgent } from "../../utils/miscUtils";
import BaseTableView from "../cmsbase/BaseTableView";
import { Button } from "primereact/button";
import { trustedDevicesTableFields } from "../../network/mockData";

const TrustedDevices = () => {
  const { toast } = useNotificationContext();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [untrusting, setUntrusting] = useState(null); // id of device being untrusted

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFromHub("trusted-device/fetch");
      const raw = response?.trustedDevices ?? [];

      setDevices(
        raw
          .sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed))
          .map((d) => ({
            id: d.id,
            device: parseUserAgent(d.userAgent),
            ipAddress: d.ipAddress,
            lastUsed: formatDate(d.lastUsed),
            expiration: formatDate(d.expiration),
          }))
      );
    } catch (e) {
      console.error("Failed to fetch trusted devices:", e);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleUntrust = async (device) => {
    setUntrusting(device.id);
    try {
      await postToHub("trusted-device/untrust", { trustedDeviceId: device.id });
      toast.current.show({
        severity: "success",
        summary: "Device Untrusted",
        detail: `"${device.device}" has been removed from trusted devices.`,
      });
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
    } catch (e) {
      console.error("Failed to untrust device:", e);
      toast.current.show({
        severity: "error",
        summary: "Failed",
        detail: "Could not untrust the device. Please try again.",
      });
    } finally {
      setUntrusting(null);
    }
  };

  const customColumn = (field, rowData) => {
    if (field !== "actions") {
      return null
    };

    const isBusy = untrusting === rowData.id;
    return (
      <Button
        label={isBusy ? "Untrusting…" : "Untrust"}
        icon={isBusy ? "pi pi-spin pi-spinner" : "pi pi-shield"}
        className="p-button-danger p-button-sm"
        disabled={isBusy}
        onClick={(e) => {
          e.stopPropagation();
          handleUntrust(rowData);
        }}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex align-items-center gap-2 p-4">
        <span>Loading trusted devices…</span>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Trusted Devices</h2>
      <BaseTableView
        data={devices}
        tableFields={trustedDevicesTableFields}
        customColumn={customColumn}
        globalFilter={false}
        emptyMessage="No trusted devices found."
      />
    </div>
  );
};

export default TrustedDevices;
